"use client"

import React, { useEffect, useState, useContext, useRef } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { FaIdCard, FaDownload, FaCamera, FaSpinner } from "react-icons/fa"
import { CircuitoContext } from "../../context/CircuitoContext"
import { UserContext } from "../../context/UserContext"
import { DomainContext } from "./LayoutCircuito"
import "../../styles/circuitoDesignSystem.css"

const API = process.env.REACT_APP_API_URL || "https://api.ticketdovaqueiro.com/api"

export default function CircuitoCarteirinha() {
  const { circuito, loadingCircuito } = useContext(CircuitoContext)
  const { user } = useContext(UserContext)
  const { basePath } = useContext(DomainContext)
  const navigate = useNavigate()

  const [inscricoes, setInscricoes] = useState([])
  const [loading, setLoading] = useState(true)
  
  const [inscricaoSelecionada, setInscricaoSelecionada] = useState(null)
  const [fotoUsuario, setFotoUsuario] = useState(null)
  const [gerando, setGerando] = useState(false)
  const [imagemFinal, setImagemFinal] = useState(null)

  const canvasRef = useRef(null)

  useEffect(() => {
    if (!user) {
      navigate(`${basePath}/inscrever-se/acesso`)
      return
    }
    if (circuito && circuito._id) {
      buscarInscricoesPagas()
    }
  }, [user, circuito, navigate, basePath])

  const buscarInscricoesPagas = async () => {
    try {
      const token = localStorage.getItem("token")
      const res = await axios.get(`${API}/circuitos/${circuito._id}/minhas-inscricoes`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const pagas = res.data.filter(i => i.statusPagamento === "pago")
      setInscricoes(pagas)
      if (pagas.length > 0) setInscricaoSelecionada(pagas[0])
    } catch (err) {
      console.error("Erro ao buscar associações")
    } finally {
      setLoading(false)
    }
  }

  const handleFotoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setFotoUsuario(event.target.result)
        setImagemFinal(null) 
      }
      reader.readAsDataURL(file)
    }
  }

  const gerarCarteirinha = async () => {
    if (!fotoUsuario || !inscricaoSelecionada) return alert("Selecione a inscrição e envie sua foto!")
    
    setGerando(true)
    const canvas = canvasRef.current
    const ctx = canvas.getContext("2d")
    
    canvas.width = 1080
    canvas.height = 1920

    try {
      // 1. Fundo
      const templateImg = await carregarImagem("/template-carteinha.jpg")
      ctx.drawImage(templateImg, 0, 0, canvas.width, canvas.height)

      // 2. Foto do Vaqueiro (Object-Fit Cover)
      const photoBoxX = 105; 
      const photoBoxY = 342; 
      const photoBoxW = 856; 
      const photoBoxH = 1048; 

      const userImg = await carregarImagem(fotoUsuario)
      
      ctx.save()
      ctx.beginPath()
      ctx.rect(photoBoxX, photoBoxY, photoBoxW, photoBoxH)
      ctx.clip() 

      const imgRatio = userImg.width / userImg.height
      const boxRatio = photoBoxW / photoBoxH
      let renderW, renderH, renderX, renderY

      if (imgRatio > boxRatio) {
        renderH = photoBoxH
        renderW = userImg.width * (photoBoxH / userImg.height)
        renderX = photoBoxX - (renderW - photoBoxW) / 2 
        renderY = photoBoxY
      } else {
        renderW = photoBoxW
        renderH = userImg.height * (photoBoxW / userImg.width)
        renderX = photoBoxX
        renderY = photoBoxY - (renderH - photoBoxH) / 2 
      }
      
      ctx.drawImage(userImg, renderX, renderY, renderW, renderH)
      ctx.restore() 

      // 3. Overlay Inferior (cima.png)
      const overlayImg = await carregarImagem("/cima.png")
      const overlayW = 590; 
      const overlayH = 400; 
      const overlayX = (canvas.width - overlayW) / 2;
      const overlayY = 1440 - (overlayH / 2) - 25; 
      ctx.drawImage(overlayImg, overlayX, overlayY, overlayW, overlayH)

      // -------------------------------------------------------------
      // 💡 4. BRASÃO DE AFILIADO (Com rotação -45 graus protegida)
      // -------------------------------------------------------------
      try {
        let afiliadoImg;
        // Tenta achar a PNG. Se você salvou como JPG, ele tenta a JPG também sem quebrar a tela!
        try {
          afiliadoImg = await carregarImagem("/associado.png");
        } catch {
          afiliadoImg = await carregarImagem("/associado.jpg"); 
        }

        if (afiliadoImg) {
          const afiliadoW = 300; 
          const afiliadoH = 450; 
          
          const afiliadoX = 25;
          const afiliadoY = 140; 

          const centroX = afiliadoX + (afiliadoW / 2);
          const centroY = afiliadoY + (afiliadoH / 2);

          ctx.save(); 
          ctx.translate(centroX, centroY);
          ctx.rotate(-30 * Math.PI / 180); // Gira pra esquerda 45º
          ctx.drawImage(afiliadoImg, -afiliadoW / 2, -afiliadoH / 2, afiliadoW, afiliadoH);
          ctx.restore(); 
        }
      } catch (err) {
        console.warn("Brasão não encontrado, prosseguindo sem ele.");
      }

      // 5. Textos e Categoria
      ctx.textAlign = "center"
      ctx.font = "bold 60px Impact, sans-serif" 
      ctx.fillStyle = "#ffffff"
      ctx.fillText(inscricaoSelecionada.categoria.toUpperCase(), canvas.width / 2, 1420)
      
      // 6. Logo do Circuito
      if (circuito?.imagem) {
        try {
          const logoUrl = `https://api.ticketdovaqueiro.com/uploads/${circuito.imagem}`
          const logoImg = await carregarImagemCross(logoUrl)
          
          const logoBoxX = 300, logoBoxY = 26, logoBoxW = 415, logoBoxH = 300
          const lRatio = logoImg.width / logoImg.height
          let lRenderW = logoBoxW, lRenderH = logoBoxH

          if (lRatio > 1) { 
              lRenderH = logoBoxW / lRatio
          } else { 
              lRenderW = logoBoxH * lRatio
          }
          
          const lRenderX = logoBoxX + (logoBoxW - lRenderW) / 2
          const lRenderY = logoBoxY + (logoBoxH - lRenderH) / 2
          
          ctx.drawImage(logoImg, lRenderX, lRenderY, lRenderW, lRenderH)
        } catch (errLogo) {
          console.warn("Falha ao desenhar a logo do circuito");
        }
      }

      ctx.font = "bold 50px Arial, sans-serif"
      ctx.fillStyle = "#ffffff"
      ctx.fillText(inscricaoSelecionada.nomeVaqueiro.toUpperCase(), canvas.width / 2, 1550)

      const isCustomDomain = window.location.hostname !== "ticketdovaqueiro.com" && window.location.hostname !== "www.ticketdovaqueiro.com" && window.location.hostname !== "localhost"
      const slugSeguro = circuito?.slug ? circuito.slug.toUpperCase() : ""
      const urlSite = isCustomDomain ? window.location.hostname.toUpperCase() : `TICKETDOVAQUEIRO.COM/CIRCUITO/${slugSeguro}`
      
      ctx.font = "italic bold 38px Arial, sans-serif"
      ctx.fillStyle = "#ffffff"
      ctx.fillText(urlSite, canvas.width / 2, 1790)

      setImagemFinal(canvas.toDataURL("image/jpeg", 0.95))

    } catch (e) {
      console.error(e)
      alert("Erro ao processar as imagens. Verifique se as imagens foram carregadas corretamente.")
    } finally {
      setGerando(false)
    }
  }

  const carregarImagem = (src) => new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })

  const carregarImagemCross = (src) => new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })

  const baixarImagem = () => {
    const link = document.createElement('a')
    link.download = `Carteirinha-${inscricaoSelecionada.nomeVaqueiro.replace(/\s+/g, '')}.jpg`
    link.href = imagemFinal
    link.click()
  }

  if (loadingCircuito || loading) return <div className="circuito-shell"><div className="loading-spinner" style={{ margin: "100px auto" }}></div></div>

  return (
    <div className="circuito-shell" style={{ padding: "40px 16px", minHeight: "80vh" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", flexDirection: "column", gap: "30px" }}>
        
        <div style={{ textAlign: "center" }}>
          <h1 style={{ fontSize: "32px", fontWeight: 800, color: "#fff", margin: "0 0 10px 0" }}>
            Carteirinha <span style={{ color: "var(--c-accent)" }}>Digital</span>
          </h1>
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "16px" }}>Gere seu ID de Sócio Oficial do <strong>{circuito?.nome}</strong></p>
        </div>

        {inscricoes.length === 0 ? (
          <div style={{ background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", padding: 30, borderRadius: 16, textAlign: "center" }}>
            <h3 style={{ color: "#ef4444", marginBottom: 10 }}>Nenhuma filiação ativa encontrada.</h3>
            <p style={{ color: "rgba(255,255,255,0.7)" }}>Você precisa estar associado e com o pagamento confirmado para gerar sua carteirinha.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 30 }}>
            
            <div style={{ background: "var(--c-card)", padding: 30, borderRadius: 16, border: "1px solid rgba(255,255,255,0.05)" }}>
              
              <div style={{ marginBottom: 25 }}>
                <label style={{ display: "block", color: "var(--c-accent)", fontWeight: "bold", marginBottom: 8 }}>1. Selecione a Filiação</label>
                <select 
                  className="c-input" 
                  style={{ width: "100%" }}
                  onChange={(e) => setInscricaoSelecionada(inscricoes.find(i => i._id === e.target.value))}
                >
                  {inscricoes.map(insc => (
                    <option key={insc._id} value={insc._id}>{insc.categoria} - {insc.nomeVaqueiro}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 25 }}>
                <label style={{ display: "block", color: "var(--c-accent)", fontWeight: "bold", marginBottom: 8 }}>2. Sua Foto de Perfil</label>
                
                <label style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: "2px dashed rgba(255,255,255,0.2)", borderRadius: 12, padding: "30px 10px", cursor: "pointer", background: "rgba(0,0,0,0.2)", transition: "0.3s" }}>
                  <FaCamera size={30} color="rgba(255,255,255,0.4)" style={{ marginBottom: 10 }} />
                  <span style={{ color: "#fff", fontSize: 14 }}>{fotoUsuario ? "Foto selecionada! Clique para trocar." : "Toque para escolher uma foto"}</span>
                  <input type="file" accept="image/jpeg, image/png, image/webp" onChange={handleFotoChange} style={{ display: "none" }} />
                </label>
              </div>

              <button 
                onClick={gerarCarteirinha} 
                disabled={gerando || !fotoUsuario}
                className="c-btn c-btn--primary" 
                style={{ width: "100%", height: 55, fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
              >
                {gerando ? <><FaSpinner className="fa-spin" /> Gerando Mágica...</> : <><FaIdCard /> Gerar Carteirinha</>}
              </button>

            </div>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
               <canvas ref={canvasRef} style={{ display: "none" }}></canvas>

               {imagemFinal ? (
                 <div style={{ animation: "fadeIn 0.5s" }}>
                    <img src={imagemFinal} alt="Carteirinha" style={{ width: "100%", maxWidth: 350, borderRadius: 16, boxShadow: "0 10px 40px rgba(0,0,0,0.5)", border: "2px solid var(--c-accent)" }} />
                    <button 
                      onClick={baixarImagem}
                      className="c-btn c-btn--primary" 
                      style={{ width: "100%", marginTop: 20, height: 50, display: "flex", gap: 10, justifyContent: "center", alignItems: "center" }}
                    >
                      <FaDownload /> Baixar Imagem HD
                    </button>
                 </div>
               ) : (
                 <div style={{ width: "100%", maxWidth: 350, aspectRatio: "9/16", background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.1)", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 20 }}>
                   <p style={{ color: "rgba(255,255,255,0.3)" }}>Preencha os dados e gere a carteirinha para visualizar aqui.</p>
                 </div>
               )}
            </div>

          </div>
        )}
      </div>
    </div>
  )
}